import { useNavigate } from 'react-router-dom';
import { useUtilities, useHistory } from '@/lib/query';
import { fmt, ts } from '@/lib/format';
import { Badge, Card, SectionTitle, TrendChart } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';
import { Icon } from '@/components/layout/Icons';

export default function Utilities() {
  const { data } = useUtilities();
  const { data: o2Hist } = useHistory('o2_pressure', 120);
  const { data: loadHist } = useHistory('bus_load', 120);
  const nav = useNavigate();
  if (!data) return <Card className="h-64 animate-pulse" />;

  return (
    <div>
      <PageBanner />
      <button onClick={() => nav('/cockpit')} className="mb-3 flex items-center gap-1.5 text-[14px] text-brand-600 hover:underline">
        <Icon name="back" size={16} /> 返回公辅保障（压力趋势）
      </button>
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">公辅设备状态</h2>
        <p className="mt-1 text-[14px] text-slate-500">高速离心风机 · 纯氧 · 备用电源</p>
        <div className="mt-1 text-[13px] text-slate-400">快照时间 {ts(data.ts)}</div>
      </div>

      <Card className="mb-5 p-6">
        <SectionTitle title="全场公辅功率与供电" />
        <div className="flex items-baseline justify-between">
          <div className="text-[15px] text-slate-600">母线总负载（kW）</div>
          <div className="text-[26px] font-bold tabular-nums text-slate-900">{fmt(data.busLoadKw, 1)} kW</div>
        </div>
        <p className="mt-1 text-[12px] text-slate-400">已含离心风机及每台 UPS（充电功率 + 逆变带载功率）；任一功率测点无效则总负载不可用</p>
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-hairline pt-4 text-[14px] md:grid-cols-3">
          <div>其中电机功率（离心风机） <b className="tabular-nums">{fmt(data.motorKw, 1)} kW</b></div>
          <div>并网柴发额定合计（Σ并网机组） <b className="tabular-nums">{fmt(data.gridDieselRatedKw, 1)} kW</b></div>
          <div>油机负载率 <b className="text-slate-400">{data.engineLoadPct === null ? '不适用' : `${data.engineLoadPct}%`}</b></div>
        </div>
      </Card>

      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <TrendChart title="主路纯氧压力趋势 (MPa)" samples={o2Hist?.[0]?.samples ?? []} unit="MPa" color="#2b6cb0" />
        <TrendChart title="母线总负载趋势 (kW)" samples={loadHist?.[0]?.samples ?? []} unit="kW" color="#17a8ab" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card className="p-6">
          <div className="mb-3 text-[15px] font-semibold text-slate-800">高速离心风机</div>
          {data.fans.map((f) => (
            <div key={f.name} className="flex items-center justify-between border-b border-hairline py-2 last:border-0">
              <div>
                <div className="text-[14px] text-slate-700">{f.name}</div>
                <div className="text-[12px] text-slate-400">实时功率 {fmt(f.powerKw, 1)} kW · 运行频率 {fmt(f.freqHz, 1)} Hz</div>
              </div>
              <Badge className={f.running ? 'bg-ok-bg text-ok-fg border-ok-border' : 'text-slate-500'}>{f.running ? '运行' : '停机'}</Badge>
            </div>
          ))}
        </Card>

        <Card className="p-6">
          <div className="mb-3 text-[15px] font-semibold text-slate-800">纯氧系统</div>
          <div className="grid grid-cols-2 gap-y-3 text-[14px]">
            <div className="text-slate-500">主管路阀</div><div className="text-right">{data.oxygen.mainValveOpen ? '开启' : '关闭'}</div>
            <div className="text-slate-500">罐体压力</div><div className="text-right tabular-nums">{data.oxygen.tankPressureMpa.toFixed(3)} MPa</div>
            <div className="text-slate-500">液位剩余</div><div className="text-right tabular-nums">{data.oxygen.levelPct} %</div>
            <div className="text-slate-500">主管道输出流量</div><div className="text-right tabular-nums">{data.oxygen.outFlowLmin} L/min</div>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="mb-3 text-[15px] font-semibold text-slate-800">发电机组 / UPS</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.gensets.map((g) => (
              <div key={g.name} className="flex items-center justify-between rounded-lg border border-hairline px-4 py-3">
                <div>
                  <div className="text-[14px] text-slate-700">{g.name}</div>
                  <div className="text-[12px] text-slate-400">单机额定 {g.ratedKw.toFixed(1)} kW · 同期并网 {g.synced ? '已同期' : '未同期'}</div>
                </div>
                <Badge>{g.state}</Badge>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.ups.map((u) => (
              <div key={u.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-[13px] text-slate-600">
                <span>{u.name}</span>
                <span className="tabular-nums">充电 {u.chargeKw} kW · 逆变带载 {u.inverterLoadKw} kW</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
